/**
 * Compare-Drawer state: persists up to 3 listing IDs in localStorage and
 * broadcasts changes via a window event so any subscriber (CompareDrawer)
 * can update without React context boilerplate.
 */
import { useEffect, useState } from "react";

const KEY = "tw_compare_ids_v1";
const MAX = 3;

function read() {
    try {
        const raw = JSON.parse(localStorage.getItem(KEY) || "[]");
        return Array.isArray(raw) ? raw.slice(0, MAX) : [];
    } catch {
        return [];
    }
}

function write(ids) {
    localStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX)));
    window.dispatchEvent(new Event("tw-compare-change"));
}

export function useCompare() {
    const [ids, setIds] = useState(read);
    useEffect(() => {
        const onChange = () => setIds(read());
        window.addEventListener("tw-compare-change", onChange);
        window.addEventListener("storage", onChange);
        return () => {
            window.removeEventListener("tw-compare-change", onChange);
            window.removeEventListener("storage", onChange);
        };
    }, []);
    const add = (id) => {
        const current = read();
        if (current.includes(id)) return { added: false, full: false };
        if (current.length >= MAX) return { added: false, full: true };
        write([...current, id]);
        return { added: true, full: false };
    };
    const remove = (id) => write(read().filter((x) => x !== id));
    const clear = () => write([]);
    const toggle = (id) => {
        const current = read();
        if (current.includes(id)) {
            remove(id);
            return { added: false };
        }
        return add(id);
    };
    return { ids, max: MAX, add, remove, clear, toggle, has: (id) => ids.includes(id) };
}
